import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Select,
  Option,
  Chip,
} from '@material-tailwind/react';
import {
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

const YEAR_RANGES = [
  { value: 0, label: 'Exact year' },
  { value: 1, label: '± 1 year' },
  { value: 2, label: '± 2 years' },
  { value: 3, label: '± 3 years' },
  { value: 5, label: '± 5 years' },
  { value: -1, label: 'All years' },
];

const PERIOD_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
  { value: 730, label: '2 years' },
  { value: 1095, label: '3 years' },
];

function AnalysisConfigPanel({ draft, onValuesChange, onDataChanged }) {
  // Original values from draft (for change detection)
  const originalYearRange = draft?.analysisConfig?.yearRange ?? 0;
  const originalPeriodDays = draft?.analysisConfig?.periodDays ?? 90;

  // Form state
  const [yearRange, setYearRange] = useState(originalYearRange);
  const [periodDays, setPeriodDays] = useState(originalPeriodDays);

  // Reset form when switching to a different draft
  useEffect(() => {
    setYearRange(draft?.analysisConfig?.yearRange ?? 0);
    setPeriodDays(draft?.analysisConfig?.periodDays ?? 90);
  }, [draft?.analysisConfig?.periodDays, draft?.analysisConfig?.yearRange, draft.draftId]);

  // Detect what changed
  const dataChanged = useMemo(() => {
    return (
      yearRange !== originalYearRange ||
      periodDays !== originalPeriodDays
    );
  }, [yearRange, periodDays, originalYearRange, originalPeriodDays]);

  // Notify parent of current values whenever they change
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange({
        yearRange,
        periodDays,
      });
    }
  }, [yearRange, periodDays, onValuesChange]);

  // Notify parent of change status
  useEffect(() => {
    if (onDataChanged) {
      onDataChanged(dataChanged);
    }
  }, [dataChanged, onDataChanged]);

  return (
    <Card className="mt-4">
      <CardBody className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Cog6ToothIcon className="h-5 w-5 text-blue-500" />
            <Typography variant="h6" color="blue-gray">
              Analysis Config
            </Typography>
          </div>
          {dataChanged && (
            <Chip
              size="sm"
              value="Modified"
              color="amber"
              variant="ghost"
            />
          )}
        </div>

        <div className="space-y-3">
          <Select
            label="Year Range (for filtering comparables)"
            value={String(yearRange)}
            onChange={(val) => setYearRange(parseInt(val, 10))}
            size="md"
            className="border-t-blue-gray-200"
          >
            {YEAR_RANGES.map((r) => (
              <Option key={String(r.value)} value={String(r.value)}>
                {r.label}
              </Option>
            ))}
          </Select>

          <Select
            label="Analysis Period (lookback window)"
            value={String(periodDays)}
            onChange={(val) => setPeriodDays(parseInt(val, 10))}
            size="md"
            className="border-t-blue-gray-200"
          >
            {PERIOD_OPTIONS.map((p) => (
              <Option key={p.value} value={String(p.value)}>
                {p.label}
              </Option>
            ))}
          </Select>
        </div>
      </CardBody>
    </Card>
  );
}

export default AnalysisConfigPanel;
